import api from '@/shared/utils/apiConfig';
import type { LeaveApproverRef } from '../utils/staffForm';

export interface Department {
  id: string;
  name: string;
  code: string;
  isSystem: boolean;
  isActive: boolean;
  leaveApprovers: LeaveApproverRef[];
  leaveFollowers: LeaveApproverRef[];
  payrollApprovers: LeaveApproverRef[];
  payrollFollowers: LeaveApproverRef[];
  attendanceApprovers: LeaveApproverRef[];
  staffCount: number;
  createdBy?: { id: string; name: string };
  updatedBy?: { id: string; name: string };
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateDepartmentPayload {
  name: string;
  leaveApprovers?: string[];
  leaveFollowers?: string[];
  payrollApprovers?: string[];
  payrollFollowers?: string[];
  attendanceApprovers?: string[];
}

export interface UpdateDepartmentPayload {
  name?: string;
  isActive?: boolean;
  leaveApprovers?: string[];
  leaveFollowers?: string[];
  payrollApprovers?: string[];
  payrollFollowers?: string[];
  attendanceApprovers?: string[];
}

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  count?: number;
  total?: number;
  page?: number;
  pages?: number;
  message?: string;
}

export interface DepartmentListParams {
  search?: string;
  status?: '' | 'active' | 'inactive';
  page: number;
  limit: number;
  sort?: string;
}

export interface DepartmentListResult {
  data: Department[];
  total: number;
  page: number;
  pages: number;
}

export const departmentAPI = {
  getAll: async (includeInactive = false): Promise<ApiResponse<Department[]>> => {
    const response = await api.get('/departments', {
      params: includeInactive ? { includeInactive: 'true' } : undefined,
    });
    return response.data;
  },

  list: async (params: DepartmentListParams): Promise<DepartmentListResult> => {
    const response = await api.get<ApiResponse<Department[]>>('/departments', {
      params: {
        includeInactive: params.status === 'active' ? undefined : 'true',
        status: params.status || undefined,
        search: params.search?.trim() || undefined,
        sort: params.sort,
        page: params.page,
        limit: params.limit,
      },
    });
    const body = response.data;
    return {
      data: body.data ?? [],
      total: body.total ?? body.data?.length ?? 0,
      page: body.page ?? params.page,
      pages: body.pages ?? 1,
    };
  },

  create: async (payload: CreateDepartmentPayload): Promise<ApiResponse<Department>> => {
    const response = await api.post('/departments', payload);
    return response.data;
  },

  update: async (
    id: string,
    payload: UpdateDepartmentPayload
  ): Promise<ApiResponse<Department>> => {
    const response = await api.put(`/departments/${id}`, payload);
    return response.data;
  },

  deactivate: async (id: string): Promise<ApiResponse<Department>> => {
    const response = await api.delete(`/departments/${id}`);
    return response.data;
  },
};
